import { Inject, Injectable } from '@angular/core';
import { NotificationsSettingsModel } from '@models/notifications/notificationSettings.model';
import { Task } from '@models/task/task.model';
import { UserSettingsModel } from '@models/user/userSettings.model';
import { environment } from 'environments/environment';
import { LocalStorageService } from 'ngx-webstorage';
import { PriceSuggesterResultsInterface } from '@app/interfaces/priceSuggester/priceSuggester.interface.type';

const keys = {
  adsClickObj: 'adsClickObj',
  authToken: 'pinploy_authToken',
  completeprofileviewed: 'pinploy_completeprofileviewed',
  createComment: 'pinploy_createComment',
  createTask: 'pinploy_createTask',
  filter: 'pinploy_filter',
  lang: 'pinploy_lang',
  notificationSettings: 'notification_settings',
  prompt_task_owners_once: 'prompt_task_owners_once',
  prompt_handyhanders_once: 'prompt_handyhanders_once',
  prompt_setup_profile_viewed: 'prompt_setup_profile_viewed',
  settingsTab: 'pinploy_settingsTab',
  socketId: 'pinploy_socketId',
  tags: 'pinploy_tags',
  task: 'pinploy_task',
  priceSuggester: 'pinploy_priceSuggester',
  user: 'pinploy_user',
  userSettings: 'user_settings',
  hasCPR: 'has_cpr',
  mitIdVerified: 'mitIdVerified',
  showCompleteProfile: 'show_complete_profile',
  userProfileLastFetch: 'user_profile_last_fetch',
} as const;

const adClickIds = [
  'gclid',
  'wbraid',
  'gbraid',
  'msclkid',
  'fbclid',
  'SsCid',
  'TTCLID',
  'twclid',
  'utm_source',
];

@Injectable()
export class StorageService {
  constructor(
    @Inject(LocalStorageService) private localStorage: LocalStorageService,
  ) {
    if (!environment.production) {
      localStorage.store(keys.prompt_handyhanders_once, true);
      localStorage.store(keys.prompt_task_owners_once, true);
    }
  }

  clear() {
    this.localStorage.clear();
    this.setCookie(keys.authToken, '', -1);
  }

  getCookie(name: string) {
    const cookieArr = document.cookie.split(';');
    for (let i = 0; i < cookieArr.length; i++) {
      const cookiePair = cookieArr[i].split('=');
      if (name === cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }

  setCookie(name: string, value: any, nDaysValid: number) {
    const d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * nDaysValid);
    const newCookie = `${name}=${value}`;
    const path = ';path=/';
    const domain = `;domain=.${document.location.hostname}`;
    const expires = `;expires=${d.toUTCString()}`;
    document.cookie = newCookie + path + domain + expires;
  }

  storeAdsClick(key: string, value: any): void {
    if (!adClickIds.includes(key)) {
      return;
    }

    const adsClickObj = this.get(keys.adsClickObj);
    if (adsClickObj) {
      if (Array.isArray(adsClickObj)) {
        if (
          !adsClickObj.some(
            (entry) =>
              entry.adsClickType === key && entry.adsClickValue === value,
          )
        ) {
          adsClickObj.push({
            adsClickType: key,
            adsClickValue: value,
            clickTimeStamp: new Date().toISOString(),
          });
        }
      } else if (
        adsClickObj.adsClickType !== key &&
        adsClickObj.adsClickValue !== value
      ) {
        this.save(keys.adsClickObj, [
          adsClickObj,
          {
            adsClickType: key,
            adsClickValue: value,
            clickTimeStamp: new Date().toISOString(),
          },
        ]);
      }
    } else {
      this.save(keys.adsClickObj, [
        {
          adsClickType: key,
          adsClickValue: value,
          clickTimeStamp: new Date().toISOString(),
        },
      ]);
    }
  }

  checkForCompleteProfile(change?: boolean): {
    value: boolean;
    initDate: Date;
    expiresDate: Date;
  } {
    if (change === false || change === true) {
      return (this.showCompleteProfile = {
        value: change,
        initDate: new Date(),
        expiresDate: this.showCompleteProfile.expiresDate,
      });
    }
    if (
      new Date() > this.showCompleteProfile.expiresDate ||
      this.showCompleteProfile.initDate > this.showCompleteProfile.expiresDate
    ) {
      return (this.showCompleteProfile = {
        value: true,
        initDate: new Date(),
        expiresDate: new Date(new Date().setDate(new Date().getDate() + 7)),
      });
    }
    return (this.showCompleteProfile = {
      value: this.showCompleteProfile.value,
      initDate: new Date(),
      expiresDate: this.showCompleteProfile.expiresDate,
    });
  }

  initStorageCookie(): { value: boolean; initDate: Date; expiresDate: Date } {
    return {
      value: true,
      initDate: new Date(),
      expiresDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    };
  }
  /**
   * Returns a unique id from cookies.
   * Value is generated if it doesnt exist in cookies.
   * Used with events to distinguish between logged in and anonymous users.
   * @returns @type UUID string
   */
  getGrowthbookUUID() {
    const COOKIE_NAME = 'gbuuid';
    const COOKIE_DAYS = 400; // 400 days is the max cookie duration for chrome

    // use the browsers crypto.randomUUID if set
    const genUUID = (): string => {
      if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
      return ([1e7].concat(-1e3, -4e3, -8e3, -1e11) as number[])
        .reduce((a, b) => a + b, 0)
        .toString(16)
        .replace(/[018]/g, (c: string) =>
          (
            parseInt(c, 10) ^
            (crypto.getRandomValues(new Uint8Array(1))[0] &
              (15 >> (parseInt(c, 10) / 4)))
          ).toString(16),
        );
    };

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };
    const setCookie = (name, value) => {
      const d = new Date();
      d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * COOKIE_DAYS);
      document.cookie = `${name}=${value};path=/;expires=${d.toUTCString()}`;
    };

    // get the existing UUID from cookie if set, otherwise create one and store it in the cookie
    if (getCookie(COOKIE_NAME)) return getCookie(COOKIE_NAME);

    const uuid = genUUID();
    setCookie(COOKIE_NAME, uuid);
    return uuid;
  }

  public save(key: string, object: any): void {
    this.localStorage.store(key, object);
  }
  public get(key: string): any {
    return this.localStorage.retrieve(key);
  }

  get authToken(): any {
    const token = this.get(keys.authToken);
    if (token && !this.getCookie(keys.authToken)) {
      this.setCookie(keys.authToken, token, 400);
    }
    return this.get(keys.authToken);
  }

  set authToken(authToken: any) {
    this.setCookie(keys.authToken, authToken, 400);
    this.save(keys.authToken, authToken);
  }

  get user(): any {
    return this.get(keys.user);
  }
  set user(user: any) {
    delete user.password;
    this.save(keys.user, user);
  }

  get adsClickObj(): any {
    return this.get(keys.adsClickObj);
  }
  set adsClickObj(value) {
    this.save(keys.adsClickObj, value);
  }

  get filter(): any {
    return this.get(keys.filter);
  }

  set filter(filter: any) {
    this.save(keys.filter, filter);
  }

  get settingsTab(): number {
    return this.get(keys.settingsTab);
  }
  set settingsTab(settingsTab: number) {
    this.save(keys.settingsTab, settingsTab);
  }

  get socketId(): any {
    return this.get(keys.socketId);
  }
  set socketId(socketId: any) {
    this.save(keys.socketId, socketId);
  }

  get showCompleteProfile(): any {
    return this.get(keys.showCompleteProfile);
  }

  set showCompleteProfile(show: any) {
    this.save(keys.showCompleteProfile, show);
  }

  get lang(): any {
    return this.get(keys.lang);
  }
  set lang(lang: any) {
    this.save(keys.lang, lang);
  }

  get tags(): string[] {
    return this.get(keys.tags);
  }
  set tags(tags: string[]) {
    this.save(keys.tags, tags);
  }

  get completeProfile(): any {
    return this.get(keys.completeprofileviewed);
  }

  set completeProfile(completeProfile) {
    this.save(keys.completeprofileviewed, completeProfile);
  }

  get userSettings(): Partial<UserSettingsModel> {
    return this.get(keys.userSettings);
  }
  set userSettings(settings: Partial<UserSettingsModel>) {
    this.save(keys.userSettings, settings);
  }

  get task(): Task {
    return this.get(keys.task);
  }
  set task(task: Task) {
    this.save(keys.task, task);
  }

  get promptTaskOwnersOnce(): boolean {
    return this.get(keys.prompt_task_owners_once);
  }
  set promptTaskOwnersOnce(value: boolean) {
    this.save(keys.prompt_task_owners_once, value);
  }

  get promptSetupProfileViewed(): boolean {
    return this.get(keys.prompt_setup_profile_viewed);
  }
  set promptSetupProfileViewed(value: boolean) {
    this.save(keys.prompt_setup_profile_viewed, value);
  }

  get notificationSettings(): Partial<NotificationsSettingsModel> {
    return this.get(keys.notificationSettings);
  }
  set notificationSettings(settings: Partial<NotificationsSettingsModel>) {
    this.save(keys.notificationSettings, settings);
  }

  get hasCPR(): boolean {
    return this.get(keys.hasCPR);
  }

  set hasCPR(hasCPR: boolean) {
    this.save(keys.hasCPR, hasCPR);
  }

  get mitIdVerified(): boolean {
    return this.get(keys.mitIdVerified);
  }

  set mitIdVerified(mitIdVerified: boolean) {
    this.save(keys.mitIdVerified, mitIdVerified);
  }

  get priceSuggester(): any {
    return this.get(keys.priceSuggester);
  }

  set priceSuggester(priceSuggester: PriceSuggesterResultsInterface) {
    this.save(keys.priceSuggester, priceSuggester);
  }

  get userProfileLastFetch(): number | null {
    return this.get(keys.userProfileLastFetch);
  }

  set userProfileLastFetch(timestamp: number) {
    this.save(keys.userProfileLastFetch, timestamp);
  }

  // get createTask(): any { return this.get(keys.createTask); }
  // set createTask(createTask: any) { this.save(keys.createTask, createTask); }

  // get createComment(): any { return this.get(keys.createComment); }
  // set createComment(createTask: any) { this.save(keys.createComment, this.createComment); }
}
